// <copyright>Copyright (c) Microsoft Corporation. All rights reserved.</copyright>

import { StrictUtils } from "@powerquery/utils";

import { type IRpcWindow } from "Rpc/IRpcWindow";
import { type RpcMessage } from "Rpc/RpcMessage";
import { type RpcMessageEventListener } from "Rpc/RpcMessageEventListener";
import * as RpcOriginUtils from "Rpc/RpcOriginUtils";
import { type RpcWindowOptions } from "Rpc/RpcWindowOptions";

export class RpcWindow implements IRpcWindow {
    private static readonly eventTypeName: string = "message";

    private readonly eventListeners: Map<RpcMessageEventListener, EventListener>;
    private readonly options: RpcWindowOptions;

    public constructor(options: RpcWindowOptions) {
        StrictUtils.assertDefined(options.targetWindow);
        StrictUtils.assertDefined(options.sourceWindow);

        this.options = options;
        this.eventListeners = new Map<RpcMessageEventListener, EventListener>();
    }

    public addListener(listener: RpcMessageEventListener): void {
        if (this.eventListeners.has(listener)) {
            throw new Error("Duplicate rpc listener added.");
        }

        const eventListener: EventListener = (event: Event) => this.handleMessageReceived(event, listener);

        this.eventListeners.set(listener, eventListener);
        this.options.sourceWindow.addEventListener(RpcWindow.eventTypeName, eventListener);
    }

    public postMessage(message: RpcMessage): void {
        const targetOrigin: string = this.options.targetOriginProvider.getTargetOrigin(message);
        this.options.targetWindow.postMessage(this.options.rpcMessageConverter.serialize(message), targetOrigin);
    }

    public removeListener(listener: RpcMessageEventListener): void {
        const eventListener: EventListener | undefined = this.eventListeners.get(listener);

        if (eventListener) {
            this.options.sourceWindow.removeEventListener(RpcWindow.eventTypeName, eventListener);
            this.eventListeners.delete(listener);
        }
    }

    private static isMessageEvent(event: Event): event is MessageEvent {
        return (event as MessageEvent).data != null;
    }

    private canProcessMessage(message: RpcMessage): boolean {
        return !!message && message.signature === this.options.rpcMessageConverter.signature;
    }

    private handleMessageReceived(event: Event, listener: RpcMessageEventListener): void {
        if (!RpcWindow.isMessageEvent(event)) {
            return;
        }

        const message: RpcMessage = this.options.rpcMessageConverter.deserialize(event as MessageEvent) as RpcMessage;

        if (!this.canProcessMessage(message)) {
            return;
        }

        if (
            this.options.allowedOrigins.some((allowedOrigin: string) =>
                RpcOriginUtils.originMatches(event.origin, allowedOrigin)
            )
        ) {
            this.options.targetOriginProvider.onMessageReceived(event.origin);
            listener(message);
        } else {
            // eslint-disable-next-line no-console
            console.warn(
                `Received message was ignored. Origin: ${JSON.stringify(
                    event.origin
                )}. Allowed origins: ${JSON.stringify(this.options.allowedOrigins)}`
            );
        }
    }
}
